import { useTranslation } from "react-i18next";
import PageTitle from "../../components/common/PageTitle/PageTitle";
import { BalanceAmount, BalanceCard, BalanceCurrency, BalanceItem, BalanceLabel, BalanceRow, BalanceSection, BalanceValue, SectionTitle, WalletContainer, WalletText } from "./Wallet.styles";
import { useEffect, useRef } from "react";
import { useDispatch } from "react-redux";
import { AppDispatch } from "../../store/store";
import { useWalletDetails, useWalletList } from "../../store/selectors/walletSelector";
import { fetchWalletDetailsThunk, fetchWalletListThunk } from "../../store/thunk/walletThunk";
import { useNavigate } from "react-router-dom";
import { useLocale } from "../../hooks/UseLocale";
import { Typography } from "@mui/material";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import { theme } from "../../theme";

const breadcrumbs = [
  {
    title: "wallet.title",
    link: "wallet",
  },
];

const Wallet = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();
  const locale = useLocale();
  const navigate = useNavigate();
  const walletList = useWalletList();
  const walletDetails = useWalletDetails();

  const listLoadRef = useRef(false);
  const detailsLoadRef = useRef(false);

  useEffect(() => {
    if (!listLoadRef.current) {
      dispatch(fetchWalletListThunk());
      listLoadRef.current = true;
    }
  }, [dispatch]);

  useEffect(() => {
    if (walletList.length > 0 && !detailsLoadRef.current) {
      dispatch(fetchWalletDetailsThunk(walletList[0].id));
      detailsLoadRef.current = true;
    }
  }, [walletList, dispatch]);

  return (
    <>
      <PageTitle breadcrumbs={breadcrumbs} title={t("wallet.title")} />
      <WalletContainer>
        <WalletText>
          {t("wallet.description")}
        </WalletText>

        <BalanceCard
          onClick={() => {
            if (walletList[0].id) {
              navigate(`/${locale}/wallet/${walletList[0].id}/transactions`);
            }
          }}
        >
          <Typography variant="subtitle2" color="textSecondary" sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <AccountBalanceWalletIcon fontSize="large" sx={{ color: theme.palette.primary.main }} />
            {t("wallet.balanceLabel")}
          </Typography>

          <BalanceRow>
            <BalanceAmount amount={walletDetails?.pointsBalance.totalCurrentBalance ?? 0}>
              {walletDetails?.pointsBalance.totalCurrentBalance}
            </BalanceAmount>
            <BalanceCurrency>
              {walletDetails?.pointsBalance.currency} 💰
            </BalanceCurrency>
          </BalanceRow>

          <BalanceSection>
            <SectionTitle>{t("wallet.currentBalances")}</SectionTitle>
            <BalanceItem>
              <BalanceLabel>{t("wallet.incomingCurrent")}:</BalanceLabel>
              <BalanceValue>{walletDetails?.pointsBalance.incomingCurrent}</BalanceValue>
            </BalanceItem>
            <BalanceItem>
              <BalanceLabel>{t("wallet.outgoingCurrent")}:</BalanceLabel>
              <BalanceValue>{walletDetails?.pointsBalance.outgoingCurrent}</BalanceValue>
            </BalanceItem>
          </BalanceSection>

          <BalanceSection>
            <SectionTitle>{t("wallet.pendingBalances")}</SectionTitle>
            <BalanceItem>
              <BalanceLabel>{t("wallet.incomingPending")}:</BalanceLabel>
              <BalanceValue>{walletDetails?.pointsBalance.incomingPending}</BalanceValue>
            </BalanceItem>
            <BalanceItem>
              <BalanceLabel>{t("wallet.outgoingPending")}:</BalanceLabel>
              <BalanceValue>{walletDetails?.pointsBalance.outgoingPending}</BalanceValue>
            </BalanceItem>
            <BalanceItem>
              <BalanceLabel>{t("wallet.totalPendingBalance")}:</BalanceLabel>
              <BalanceValue>{walletDetails?.pointsBalance.totalPendingBalance}</BalanceValue>
            </BalanceItem>
          </BalanceSection>
        </BalanceCard>
      </WalletContainer>
    </>
  );
};

export default Wallet;
